import React, { useState, useEffect } from 'react';
import './LocationPopup.css';
import api from '../services/api';
import LiveMap from './LiveMap';

const GOOGLE_MAP_KEY = process.env.REACT_APP_GOOGLE_MAP_KEY;

const LocationPopup = ({ child, onClose, lockInfo, onSetPositionLock, onCancelPositionLock }) => {
  const [locationData, setLocationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    loadLocationData();
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, [child.id]);

  const fetchAddress = async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`);
      const data = await response.json();
      return data.display_name || 'Address not available';
    } catch (error) {
      console.error('Error fetching address:', error);
      return 'Address not available';
    }
  };

  const loadLocationData = async () => {
    setLoading(true);
    try {
      const data = await api.getLastLocation(child.device.id);
      
      // Fetch live address if coordinates are available
      if (data.latitude && data.longitude) {
        const liveAddress = await fetchAddress(data.latitude, data.longitude);
        data.address = liveAddress;
      }
      
      setLocationData(data);
    } catch (error) {
      console.error('Error loading location:', error);
      // Mock data for demo
      const mockData = {
        latitude: 19.0760,
        longitude: 72.8777,
        timestamp: new Date(Date.now() - 300000).toISOString(), // 5 minutes ago
        address: 'Mumbai, Maharashtra, India'
      };
      
      try {
        // Try to fetch live address for mock data
        const liveAddress = await fetchAddress(mockData.latitude, mockData.longitude);
        mockData.address = liveAddress;
      } catch (addressError) {
        console.log('Using fallback address');
      }
      
      setLocationData(mockData);
    }
    setLoading(false);
  };

  const getTimeDifference = () => {
    if (!locationData?.timestamp) return 'Unknown';
    
    const lastUpdate = new Date(locationData.timestamp);
    const diffMs = currentTime - lastUpdate;
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) return 'Just now';
    return `${diffMinutes} min ago`;
  };

  const openWhatsAppFallback = (message) => {
    // Custom HTTPS URL for app interception / WebView-friendly fallback
    window.open(`https://openwhatsapp?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="location-popup-overlay">
      <div className="location-popup">
        <div className="popup-header">
          <h2>📍 {child.name}'s Location</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="popup-content">
          {loading ? (
            <div className="loading-section">
              <div className="spinner"></div>
              <p>Loading location...</p>
            </div>
          ) : (
            <>
              <div className="location-info">
                <div className="map-container">
                  {locationData?.latitude && locationData?.longitude ? (
                    GOOGLE_MAP_KEY ? (
                      <iframe
                        src={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAP_KEY}&q=${Number(locationData.latitude)},${Number(locationData.longitude)}&zoom=17&maptype=roadmap`}
                        width="100%"
                        height="100%"
                        style={{ border: 0, borderRadius: '15px' }}
                        title="Google Maps Location"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      ></iframe>
                    ) : (
                      <iframe
                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(locationData.longitude) - 0.0008},${Number(locationData.latitude) - 0.0008},${Number(locationData.longitude) + 0.0008},${Number(locationData.latitude) + 0.0008}&layer=mapnik&marker=${locationData.latitude},${locationData.longitude}&zoom=17`}
                        width="100%"
                        height="100%"
                        style={{ border: 0, borderRadius: '15px' }}
                        title="Location Map"
                        allowFullScreen
                      ></iframe>
                    )
                  ) : (
                    <div className="map-placeholder">
                      <div className="placeholder-content">
                        <div className="placeholder-icon">📍</div>
                        <p>Location not available</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="time-info">
                <div className="time-row">
                  <span className="time-value">
                    {locationData?.latitude ? Number(locationData.latitude).toFixed(6) : '0.000000'}, {locationData?.longitude ? Number(locationData.longitude).toFixed(6) : '0.000000'}
                  </span>
                </div>
                <div className="time-row">
                  <span className="time-value">{locationData?.address || 'Address not available'}</span>
                </div>
                <div className="time-row difference">
                  <span className="time-value">Last seen: {getTimeDifference()}</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="popup-actions">
          <div className="action-buttons">
            <button className="refresh-btn" onClick={loadLocationData} disabled={loading}>
              🔄 Refresh Location
            </button>
            <button
              className="share-btn"
              onClick={() => {
                if (!locationData?.latitude || !locationData?.longitude) {
                  alert('Location not available to share yet.');
                  return;
                }
                const lat = Number(locationData.latitude).toFixed(6);
                const lng = Number(locationData.longitude).toFixed(6);
                const link = `https://www.google.com/maps?q=${lat},${lng}`;
                const text = `${child.name}'s location:\n${link}`;
                if (navigator.share) {
                  navigator.share({ title: `${child.name}'s location`, text, url: link })
                    .catch(() => {
                      openWhatsAppFallback(text);
                    });
                } else {
                  openWhatsAppFallback(text);
                }
              }}
            >
              📤 Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationPopup;